/**
 * City map — 25 x 25 plot grid, build queue and demolition visualisation. spec
 * §9, §10.
 *
 * Reads a state snapshot and draws. It never touches the simulation: no
 * mutation, no timers, no randomness. Every value on screen comes from the
 * `GameState` passed in and the layout derived from the canvas box.
 */

import { isFactoryPlot } from '../../sim/city';
import { CITY_GRID_SIZE, FACTORY_MAX, FACTORY_MIN } from '../../sim/constants';
import type { GameState, Plot, PlotCoord } from '../../sim/types';
import { BUILDING_COLORS, COLORS, prepareCanvas } from '../theme';

export interface CityMapLayout {
  cellSize: number;
  originX: number;
  originY: number;
}

/** A plot range selection, inclusive on both ends, unnormalised. */
type PlotRangeSelection = { x0: number; y0: number; x1: number; y1: number };

/** Compute the layout that fits a 25x25 grid into the given CSS pixel box. */
export function cityMapLayout(cssWidth: number, cssHeight: number): CityMapLayout {
  const cellSize = Math.floor(Math.min(cssWidth, cssHeight) / CITY_GRID_SIZE);
  const gridPixels = cellSize * CITY_GRID_SIZE;
  // Centre the block rather than pin it to a corner, so the grid does not hug
  // one edge of the panel when the box is not exactly square.
  const originX = Math.round((cssWidth - gridPixels) / 2);
  const originY = Math.round((cssHeight - gridPixels) / 2);
  return { cellSize, originX, originY };
}

/** Convert a pixel position (CSS px, relative to canvas) to a plot coordinate, or null if outside the grid. */
export function plotAtPixel(layout: CityMapLayout, px: number, py: number): PlotCoord | null {
  const { cellSize, originX, originY } = layout;
  if (cellSize < 1) return null;

  const localX = px - originX;
  const localY = py - originY;
  if (localX < 0 || localY < 0) return null;

  const x = Math.floor(localX / cellSize);
  const y = Math.floor(localY / cellSize);
  if (x < 0 || x >= CITY_GRID_SIZE || y < 0 || y >= CITY_GRID_SIZE) return null;

  return { x, y };
}

function clampFraction(value: number): number {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}


/**
 * A plot, rounded to match the controls on the rest of the console.
 *
 * Inset by a pixel so neighbouring plots read as separate lit objects rather
 * than as one continuous slab of colour.
 */
function tracePlot(
  ctx: CanvasRenderingContext2D,
  cellX: number,
  cellY: number,
  cellSize: number,
): void {
  const inset = Math.min(1.5, cellSize * 0.08);
  const size = cellSize - inset * 2;
  const radius = Math.min(size / 2, Math.max(2, cellSize * 0.26));
  ctx.beginPath();
  ctx.roundRect(cellX + inset, cellY + inset, size, size, radius);
}

/** Fill the current path, then trace it again as a lit edge. */
function litFill(
  ctx: CanvasRenderingContext2D,
  colour: string,
  fillAlpha: number,
  edgeAlpha: number,
): void {
  ctx.globalAlpha = fillAlpha;
  ctx.fillStyle = colour;
  ctx.fill();
  ctx.globalAlpha = 1;

  ctx.save();
  ctx.shadowColor = colour;
  ctx.shadowBlur = 10;
  ctx.globalAlpha = edgeAlpha;
  ctx.strokeStyle = colour;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
}

/**
 * Draw one occupied plot's interior. The factory block and empty plots are
 * handled by the caller directly, since neither carries a `Plot` record.
 */
function drawOccupiedPlot(
  ctx: CanvasRenderingContext2D,
  plot: Plot,
  cellX: number,
  cellY: number,
  cellSize: number,
): void {
  const buildingColor = BUILDING_COLORS[plot.buildingType];

  switch (plot.state) {
    case 'queued': {
      // Outline only: planned, not present. The building's own colour says what
      // is coming without it reading as already built.
      tracePlot(ctx, cellX, cellY, cellSize);
      litFill(ctx, buildingColor, 0.1, 0.55);
      break;
    }
    case 'building': {
      // Progress reads as a level filling the cell from the bottom, like a
      // gauge, rather than a generic bar — it doubles as a rough silhouette of
      // the building rising out of the ground. Clipped to the rounded plot so
      // the rising level keeps the shape.
      tracePlot(ctx, cellX, cellY, cellSize);
      litFill(ctx, buildingColor, 0.12, 0.6);

      const ratio = plot.totalTicks > 0 ? clampFraction(plot.progressTicks / plot.totalTicks) : 0;
      ctx.save();
      tracePlot(ctx, cellX, cellY, cellSize);
      ctx.clip();
      ctx.globalAlpha = 0.85;
      ctx.fillStyle = buildingColor;
      ctx.fillRect(cellX, cellY + cellSize * (1 - ratio), cellSize, cellSize * ratio);
      ctx.restore();
      break;
    }
    case 'complete': {
      tracePlot(ctx, cellX, cellY, cellSize);
      litFill(ctx, buildingColor, 0.72, 0.9);
      break;
    }
    case 'demolishing': {
      tracePlot(ctx, cellX, cellY, cellSize);
      litFill(ctx, COLORS.demolishing, 0.45, 0.9);
      break;
    }
  }
}

/** Grid separators as a single stroked path, so 625 cells cost one draw call. */
function drawGridLines(ctx: CanvasRenderingContext2D, layout: CityMapLayout): void {
  const { cellSize, originX, originY } = layout;
  const gridPixels = cellSize * CITY_GRID_SIZE;

  // Quieter than it was: the plots are lit objects now, and a hard lattice
  // behind them fights with their edges.
  ctx.globalAlpha = 0.55;
  ctx.strokeStyle = COLORS.gridLine;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 0; i <= CITY_GRID_SIZE; i++) {
    // A 1px line is crisp only when centred on a half-pixel, else it blurs
    // across the two device pixels either side of the integer boundary.
    const x = originX + i * cellSize + 0.5;
    ctx.moveTo(x, originY);
    ctx.lineTo(x, originY + gridPixels);
  }
  for (let i = 0; i <= CITY_GRID_SIZE; i++) {
    const y = originY + i * cellSize + 0.5;
    ctx.moveTo(originX, y);
    ctx.lineTo(originX + gridPixels, y);
  }
  ctx.stroke();
  ctx.globalAlpha = 1;
}

function drawSelection(
  ctx: CanvasRenderingContext2D,
  layout: CityMapLayout,
  selection: PlotRangeSelection,
): void {
  const { cellSize, originX, originY } = layout;
  const minX = Math.min(selection.x0, selection.x1);
  const maxX = Math.max(selection.x0, selection.x1);
  const minY = Math.min(selection.y0, selection.y1);
  const maxY = Math.max(selection.y0, selection.y1);

  const left = originX + minX * cellSize;
  const top = originY + minY * cellSize;
  const width = (maxX - minX + 1) * cellSize;
  const height = (maxY - minY + 1) * cellSize;

  // A 2px stroke centred on an integer coordinate splits evenly across pixels
  // either side, so — unlike the 1px grid — no half-pixel offset is needed.
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(left, top, width, height, Math.min(18, cellSize * 0.4));
  ctx.shadowColor = COLORS.accent;
  ctx.shadowBlur = 18;
  ctx.strokeStyle = COLORS.accent;
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.restore();
}

/** Draw the whole city map. */
export function drawCityMap(
  canvas: HTMLCanvasElement,
  state: GameState,
  cssWidth: number,
  cssHeight: number,
  devicePixelRatio: number,
  selection?: PlotRangeSelection | null,
): void {
  const ctx = prepareCanvas(canvas, cssWidth, cssHeight, devicePixelRatio);
  if (ctx === null) return;

  ctx.fillStyle = COLORS.background;
  ctx.fillRect(0, 0, cssWidth, cssHeight);

  const layout = cityMapLayout(cssWidth, cssHeight);
  const { cellSize, originX, originY } = layout;
  if (cellSize < 1) return;

  const grid = state.city.grid;

  for (let y = 0; y < CITY_GRID_SIZE; y++) {
    const row = grid[y];
    for (let x = 0; x < CITY_GRID_SIZE; x++) {
      const cellX = originX + x * cellSize;
      const cellY = originY + y * cellSize;

      if (isFactoryPlot({ x, y })) {
        ctx.fillStyle = COLORS.factory;
        ctx.fillRect(cellX, cellY, cellSize, cellSize);
        continue;
      }

      const plot = row?.[x] ?? null;
      if (plot === null) {
        ctx.fillStyle = COLORS.empty;
        ctx.fillRect(cellX, cellY, cellSize, cellSize);
        continue;
      }

      drawOccupiedPlot(ctx, plot, cellX, cellY, cellSize);
    }
  }

  // Grid lines before the label so the label sits legibly on top rather than
  // having a separator line cut through the text.
  drawGridLines(ctx, layout);

  // Below this, "FACTORY" would just be noise smeared across a handful of
  // pixels, so skip it rather than draw something unreadable.
  if (cellSize >= 10) {
    const blockPlots = FACTORY_MAX - FACTORY_MIN + 1;
    const blockPixels = blockPlots * cellSize;
    const centerX = originX + FACTORY_MIN * cellSize + blockPixels / 2;
    const centerY = originY + FACTORY_MIN * cellSize + blockPixels / 2;

    ctx.fillStyle = COLORS.background;
    ctx.font = `bold ${Math.round(cellSize * 0.5)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('FACTORY', centerX, centerY);
  }

  // Selection is drawn last so it always sits on top of cells, grid, and label.
  if (selection != null) {
    drawSelection(ctx, layout, selection);
  }
}
